import styled from '@emotion/styled';
import { uiTheme as t, ProgressBar } from 'Components/ui';
import useTranslation from 'Hooks/useTranslation';
import {
  Achievement1IconC,
  Achievement1IconNC,
  Achievement2IconC,
  Achievement2IconNC,
  Achievement3IconC,
  Achievement3IconNC,
  Achievement4IconC,
  Achievement4IconNC,
} from 'Images';
import type { AchievementKey, AchievementProgressI } from 'types';

interface Props {
  achievements: AchievementProgressI[];
}

/**
 * Iconos por clave del logro.
 *
 * Antes la unión era por la posición del arreglo: reordenar las llamadas del
 * backend repintaba cada insignia con el nombre y el icono de otra. Con la
 * clave como llave, el orden en que lleguen deja de importar.
 */
const ICONS: Record<AchievementKey, { earned: string; locked: string }> = {
  usedAppFirstTime: { earned: Achievement4IconC, locked: Achievement4IconNC },
  passedFirstCourse: { earned: Achievement2IconC, locked: Achievement2IconNC },
  passedCourseOutsideDevelopmentPlan: { earned: Achievement3IconC, locked: Achievement3IconNC },
  finishDevelopmentPlan: { earned: Achievement1IconC, locked: Achievement1IconNC },
};

const List = styled.ul({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.md,
  overflow: 'hidden',
});

const Row = styled.li({
  display: 'flex',
  alignItems: 'center',
  gap: 15,
  padding: '15px 20px',
  borderBottom: `1px solid ${t.color.rowBorder}`,
  '&:last-of-type': { borderBottom: 'none' },
  '&:hover': { background: t.color.rowHoverBg },
  // Bajo el corte estrecho la descripción y la barra bajan a su propia línea.
  [`@media (max-width: ${t.breakpoint.md})`]: { flexWrap: 'wrap', gap: 10 },
});

const Mark = styled.img({ width: 40, height: 40, flex: 'none', borderRadius: '50%' });

const Identity = styled.div({
  width: 200,
  flex: 'none',
  [`@media (max-width: ${t.breakpoint.md})`]: { width: 'auto', flex: 1 },
});

const Name = styled.div({ fontFamily: t.font, fontSize: 13, fontWeight: 600, color: t.color.title });

const State = styled.div({ marginTop: 3, fontFamily: t.font, fontSize: 11.5, color: t.color.muted });

const Description = styled.div({
  flex: 1,
  minWidth: 180,
  fontFamily: t.font,
  fontSize: 12,
  color: t.color.text,
});

const Meter = styled.div({
  width: 140,
  flex: 'none',
  [`@media (max-width: ${t.breakpoint.md})`]: { width: '100%' },
});

/**
 * Insignias del empleado, una por fila.
 *
 * El backend solo entrega `key`, `completed` y `progress`. El nombre, la
 * descripción y el icono son copia de interfaz y viven donde vive el resto del
 * copy: en las traducciones y en el mapa de imágenes.
 */
const BadgeList = ({ achievements }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const stateLabel = (achievement: AchievementProgressI) => {
    if (achievement.completed) return t2('badge_state_earned');
    return achievement.progress > 0 ? t2('badge_state_progress') : t2('badge_state_locked');
  };

  return (
    <List>
      {achievements
        .filter(achievement => ICONS[achievement.key])
        .map(achievement => (
          <Row key={achievement.key}>
            <Mark src={achievement.completed ? ICONS[achievement.key].earned : ICONS[achievement.key].locked} alt="" />
            <Identity>
              <Name>{t2(`badge_${achievement.key}`)}</Name>
              <State>{stateLabel(achievement)}</State>
            </Identity>
            <Description>{t2(`badge_${achievement.key}_description`)}</Description>
            <Meter>
              <ProgressBar value={achievement.progress} label={`${Math.round(achievement.progress)}%`} />
            </Meter>
          </Row>
        ))}
    </List>
  );
};

export default BadgeList;
